/**
 * @name TotemInterface
 * @author Keven Adan Bezerra | kevenadanb
 * @description Interface principal do totem para seleção de produtos com layout de 3 colunas
 * @package src/components/pages/TotemInterface.tsx
 */

import ProdutoImagem from "@/components/common/ProdutoImagem";
import RestricaoAlimentarModal from "@/components/modals/RestricaoAlimentarModal";
import { useTotemInterface } from "@/hooks/useTotemInterface";
import useThemeContext from "@/themes/hooks/useThemeContext";
import { getGlassCard } from "@/themes/styles/glassStyles";
import {
  Add as AddIcon,
  ShoppingCart as CartIcon,
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React from "react";

interface TotemInterfaceProps {
  onIrParaCarrinho: () => void;
}

const TotemInterface: React.FC<TotemInterfaceProps> = ({
  onIrParaCarrinho,
}) => {
  const { theme } = useThemeContext();

  // Usando o hook personalizado para melhor organização e performance
  const {
    alunoAtual,
    categoriaAtiva,
    categorias,
    produtosFiltrados,
    itens,
    handleCategoriaChange,
    handleAdicionarProduto,
    handleRemoverProduto,
    limparCarrinho,
    getQuantidadeNoCarrinho,
    podeFinalizarCompra,
    saldoInsuficiente,
    estatisticasCarrinho,
    modalRestricao,
    handleFecharModalRestricao,
  } = useTotemInterface();

  return (
    <Box
      sx={{
        height: "100vh",
        p: 2,
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl" sx={{ height: "100%" }}>
        <Grid container spacing={3} sx={{ height: "100%" }}>
          {/* Painel do Aluno e Carrinho - 1/3 da tela */}
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
            sx={{ height: "100%", display: "flex" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {/* Card de Informações do Aluno */}
              <Card
                sx={{
                  p: 3,
                  ...getGlassCard(theme, "medium"),
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}40, ${theme.palette.primary.dark}20)`,
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: "auto",
                      mb: 2,
                      bgcolor: "rgba(255,255,255,0.2)",
                      fontSize: "2rem",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: "2rem" }} />
                  </Avatar>

                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {alunoAtual?.nome}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      R$ {alunoAtual?.saldo?.toFixed(2) || "0,00"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Saldo disponível
                    </Typography>
                  </Box>

                  {/* Restrições do Aluno */}
                  {alunoAtual?.restricoes_alimentares &&
                    alunoAtual.restricoes_alimentares.length > 0 && (
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          gutterBottom
                          sx={{ opacity: 0.9 }}
                        >
                          Restrições Alimentares:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          {alunoAtual.restricoes_alimentares.map(
                            (restricao, index) => (
                              <Chip
                                key={index}
                                label={restricao}
                                size="small"
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.2)",
                                  color: "white",
                                  backdropFilter: "blur(10px)",
                                }}
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    )}
                </Box>
              </Card>

              {/* Card do Carrinho de Compras */}
              <Card
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  maxHeight: "60vh", // Altura máxima mais conservadora
                  ...getGlassCard(theme, "light"),
                }}
              >
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 2, // Reduzir padding para economizar espaço
                    overflow: "hidden",
                    maxHeight: "55vh", // Altura máxima do conteúdo
                    "&:last-child": { pb: 2 }, // Padding bottom menor
                  }}
                >
                  {/* Header do Carrinho */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <CartIcon color="primary" />
                      Meu Carrinho
                      {estatisticasCarrinho.totalItens > 0 && (
                        <Badge
                          badgeContent={estatisticasCarrinho.totalItens}
                          color="primary"
                          sx={{
                            "& .MuiBadge-badge": {
                              fontSize: "0.8rem",
                              fontWeight: "bold",
                            },
                          }}
                        />
                      )}
                    </Typography>
                    {itens.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={limparCarrinho}
                        sx={{
                          color: theme.palette.error.main,
                          "&:hover": {
                            bgcolor: theme.palette.error.light + "20",
                          },
                        }}
                        title="Limpar carrinho"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Lista de Itens do Carrinho */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      mb: 1, // Reduzir margin bottom
                      minHeight: 0,
                      maxHeight: "20vh", // Altura ainda menor
                      "&::-webkit-scrollbar": {
                        width: "4px", // Scrollbar mais fino
                      },
                      "&::-webkit-scrollbar-track": {
                        background: theme.palette.grey[100],
                        borderRadius: "2px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: theme.palette.grey[400],
                        borderRadius: "2px",
                        "&:hover": {
                          background: theme.palette.grey[600],
                        },
                      },
                    }}
                  >
                    {itens.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <CartIcon
                          sx={{ fontSize: "3rem", mb: 2, opacity: 0.3 }}
                        />
                        <Typography variant="h6" gutterBottom>
                          Carrinho vazio
                        </Typography>
                        <Typography variant="body2">
                          Selecione produtos para adicionar
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {itens.map((item) => (
                          <Card
                            key={item.id}
                            variant="outlined"
                            sx={{
                              p: 2,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: theme.shadows[2],
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  sx={{ mb: 0.5 }}
                                >
                                  {item.nome}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  fontWeight="medium"
                                >
                                  R$ {item.preco.toFixed(2)} × {item.quantidade}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoverProduto(item.id)}
                                  sx={{
                                    bgcolor: theme.palette.error.main,
                                    color: "white",
                                    width: 32,
                                    height: 32,
                                    "&:hover": {
                                      bgcolor: theme.palette.error.dark,
                                    },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>

                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  sx={{
                                    minWidth: 32,
                                    textAlign: "center",
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  {item.quantidade}
                                </Typography>

                                <IconButton
                                  size="small"
                                  onClick={() => handleAdicionarProduto(item)}
                                  sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: "white",
                                    width: 32,
                                    height: 32,
                                    "&:hover": {
                                      bgcolor: theme.palette.primary.dark,
                                    },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Resumo e Finalização */}
                  {estatisticasCarrinho.temItens && (
                    <Box
                      sx={{
                        flexShrink: 0, // Não permite que encolha
                        mt: "auto", // Empurra para o final
                      }}
                    >
                      <Divider sx={{ mb: 1 }} />

                      <Box
                        sx={{
                          mb: 1,
                          p: 1.5,
                          bgcolor: theme.palette.grey[50],
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            Total do Pedido:
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary"
                          >
                            R$ {estatisticasCarrinho.valorTotal.toFixed(2)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Saldo restante:
                          </Typography>
                          <Typography
                            variant="caption"
                            color={saldoInsuficiente ? "error" : "success.main"}
                            fontWeight="medium"
                          >
                            R$ {estatisticasCarrinho.saldoRestante.toFixed(2)}
                          </Typography>
                        </Box>

                        {saldoInsuficiente && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            ⚠️ Saldo insuficiente
                          </Typography>
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<CreditCardIcon />}
                        onClick={onIrParaCarrinho}
                        disabled={!podeFinalizarCompra}
                        sx={{
                          height: 48, // Altura menor
                          fontSize: "1rem",
                          fontWeight: "bold",
                          textTransform: "none",
                          borderRadius: 2,
                          boxShadow: theme.shadows[4],
                          mb: 1, // Margin bottom menor
                          "&:hover": {
                            boxShadow: theme.shadows[8],
                            transform: "translateY(-2px)",
                          },
                          "&:disabled": {
                            opacity: 0.6,
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        Finalizar Compra
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Área de Produtos - 2/3 da tela */}
          <Grid item xs={12} md={8} lg={9} sx={{ height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Card de Categorias */}
              <Card sx={{ ...getGlassCard(theme, "light") }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Categorias de Produtos
                  </Typography>
                  <Grid container spacing={1}>
                    {categorias.map((categoria) => (
                      <Grid item key={categoria.id} xs={6} sm={4} md={3} lg={2}>
                        <Button
                          fullWidth
                          variant={
                            categoriaAtiva === categoria.id
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => handleCategoriaChange(categoria.id)}
                          sx={{
                            height: 80,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            fontSize: "0.8rem",
                            textTransform: "none",
                            borderRadius: 2,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: theme.shadows[4],
                            },
                          }}
                        >
                          <Typography variant="h5" sx={{ mb: 0.5 }}>
                            {categoria.icone}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {categoria.nome}
                          </Typography>
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>

              {/* Card de Grid de Produtos */}
              <Card
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  ...getGlassCard(theme, "light"),
                }}
              >
                <CardContent
                  sx={{
                    p: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 2, flexShrink: 0 }}
                  >
                    Produtos Disponíveis
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      minHeight: 0,
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: theme.palette.grey[100],
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: theme.palette.grey[400],
                        borderRadius: "4px",
                        "&:hover": {
                          background: theme.palette.grey[600],
                        },
                      },
                    }}
                  >
                    <Grid container spacing={2}>
                      {produtosFiltrados.map((produto) => {
                        const quantidadeNoCarrinho = getQuantidadeNoCarrinho(
                          produto.id,
                        );
                        const podeComprar =
                          !alunoAtual?.temRestricaoGeral &&
                          !alunoAtual?.produtosBloqueadosIds?.includes(
                            produto.id,
                          ) &&
                          !produto.restricoes?.some((restricao) =>
                            alunoAtual?.restricoes_alimentares?.includes(
                              restricao.nome,
                            ),
                          );

                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            key={produto.id}
                          >
                            <Card
                              sx={{
                                ...getGlassCard(theme, "light"),
                                height: {
                                  xs: "340px",
                                  sm: "360px",
                                  md: "380px",
                                  lg: "400px",
                                },
                                opacity: podeComprar ? 1 : 0.6,
                                border:
                                  quantidadeNoCarrinho > 0
                                    ? `3px solid ${theme.palette.primary.main}`
                                    : `1px solid ${theme.palette.divider}`,
                                cursor: podeComprar ? "pointer" : "not-allowed",
                                "&:hover": podeComprar
                                  ? {
                                      transform: "translateY(-4px)",
                                      boxShadow: theme.shadows[8],
                                      border: `2px solid ${theme.palette.primary.main}`,
                                    }
                                  : {},
                              }}
                              onClick={() =>
                                podeComprar && handleAdicionarProduto(produto)
                              }
                            >
                              {/* Badges */}
                              {!podeComprar && (
                                <Chip
                                  label="⚠️ Restrito"
                                  color="error"
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 1,
                                  }}
                                />
                              )}

                              {produto.combo && (
                                <Chip
                                  label="🍽️ Combo"
                                  color="secondary"
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    zIndex: 1,
                                  }}
                                />
                              )}

                              <CardContent
                                sx={{
                                  p: 2,
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {/* Imagem do Produto */}
                                <ProdutoImagem
                                  src={produto.imagem}
                                  alt={produto.nome}
                                  categoria={produto.categoria_nome}
                                  produtoId={produto.id}
                                  isCombo={produto.combo}
                                  sx={{
                                    mb: 2,
                                    bgcolor: theme.palette.grey[100],
                                    background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[200]} 100%)`,
                                  }}
                                />

                                {/* Informações do Produto */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    gutterBottom
                                    sx={{
                                      fontSize: {
                                        xs: "1rem",
                                        sm: "1.1rem",
                                        md: "1.25rem",
                                      },
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {produto.nome}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      mb: 2,
                                      minHeight: { xs: 32, sm: 36, md: 40 },
                                      lineHeight: 1.3,
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.8rem",
                                        md: "0.875rem",
                                      },
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {produto.descricao}
                                  </Typography>

                                  <Typography
                                    variant="h5"
                                    fontWeight="bold"
                                    color="primary"
                                    sx={{
                                      mb: 2,
                                      fontSize: {
                                        xs: "1.2rem",
                                        sm: "1.4rem",
                                        md: "1.5rem",
                                      },
                                    }}
                                  >
                                    R$ {produto.preco.toFixed(2)}
                                  </Typography>
                                </Box>

                                {/* Controles de Quantidade */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    mt: "auto",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {quantidadeNoCarrinho === 0 ? (
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAdicionarProduto(produto);
                                      }}
                                      disabled={!podeComprar}
                                      sx={{
                                        bgcolor: theme.palette.primary.main,
                                        color: "white",
                                        width: { xs: 40, sm: 44, md: 48 },
                                        height: { xs: 40, sm: 44, md: 48 },
                                        "&:hover": {
                                          bgcolor: theme.palette.primary.dark,
                                          transform: "scale(1.1)",
                                        },
                                        "&:disabled": {
                                          bgcolor: theme.palette.grey[300],
                                          color: theme.palette.grey[500],
                                        },
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <AddIcon
                                        sx={{
                                          fontSize: {
                                            xs: "1.2rem",
                                            sm: "1.4rem",
                                            md: "1.5rem",
                                          },
                                        }}
                                      />
                                    </IconButton>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoverProduto(produto.id);
                                        }}
                                        sx={{
                                          bgcolor: theme.palette.error.main,
                                          color: "white",
                                          width: { xs: 32, sm: 36, md: 40 },
                                          height: { xs: 32, sm: 36, md: 40 },
                                          "&:hover": {
                                            bgcolor: theme.palette.error.dark,
                                            transform: "scale(1.1)",
                                          },
                                          transition: "all 0.2s ease",
                                        }}
                                      >
                                        <RemoveIcon
                                          sx={{
                                            fontSize: {
                                              xs: "1rem",
                                              sm: "1.1rem",
                                              md: "1.25rem",
                                            },
                                          }}
                                        />
                                      </IconButton>

                                      <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                          minWidth: { xs: 32, sm: 36, md: 40 },
                                          textAlign: "center",
                                          color: theme.palette.primary.main,
                                          bgcolor:
                                            theme.palette.primary.light + "20",
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.5,
                                          fontSize: {
                                            xs: "1.1rem",
                                            sm: "1.2rem",
                                            md: "1.5rem",
                                          },
                                        }}
                                      >
                                        {quantidadeNoCarrinho}
                                      </Typography>

                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAdicionarProduto(produto);
                                        }}
                                        sx={{
                                          bgcolor: theme.palette.primary.main,
                                          color: "white",
                                          width: { xs: 32, sm: 36, md: 40 },
                                          height: { xs: 32, sm: 36, md: 40 },
                                          "&:hover": {
                                            bgcolor: theme.palette.primary.dark,
                                            transform: "scale(1.1)",
                                          },
                                          transition: "all 0.2s ease",
                                        }}
                                      >
                                        <AddIcon
                                          sx={{
                                            fontSize: {
                                              xs: "1rem",
                                              sm: "1.1rem",
                                              md: "1.25rem",
                                            },
                                          }}
                                        />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Modal de Restrição Alimentar */}
      <RestricaoAlimentarModal
        open={modalRestricao.open}
        onClose={handleFecharModalRestricao}
        produto={modalRestricao.produto}
        restricoesAluno={alunoAtual?.restricoes_alimentares || []}
      />
    </Box>
  );
};

export default TotemInterface;
